/**
 * Isometrische ("2.5D") Projektion des Hallenplans.
 *
 * Plan-Koordinaten: x nach rechts, y nach unten (wie SVG), z nach oben. Der Plan wird um `angle` gedreht und
 * um `tilt` gestaucht; der Betrachter schaut von vorn-rechts (positive x- und y-Richtung) schräg von oben.
 * Alle Funktionen sind rein und werden einmal beim Aufbau der Szene aufgerufen.
 */

export const DEFAULT_VIEW = {
  angle: 35 * Math.PI / 180,
  tilt: 0.6
}

export function createProjection(view = DEFAULT_VIEW) {
  const cos = Math.cos(view.angle)
  const sin = Math.sin(view.angle)

  return {
    /** Punkt (x, y, z) des Plans → [sx, sy] auf dem Bildschirm. */
    project(x, y, z = 0) {
      return [x * cos - y * sin, (x * sin + y * cos) * view.tilt - z]
    },
    /** Richtung "zum Betrachter" in der Planebene (entspricht "nach unten" auf dem Bildschirm). */
    toward: [sin, cos],
    tilt: view.tilt
  }
}

/** Rechteck → Polygon im Uhrzeigersinn (bei y nach unten). */
export function rectPoints({ x, y, w, h }) {
  return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]]
}

function signedArea(points) {
  let area = 0

  for (let i = 0; i < points.length; i++) {
    const [x0, y0] = points[i]
    const [x1, y1] = points[(i + 1) % points.length]

    area += x0 * y1 - x1 * y0
  }

  return area / 2
}

export function bboxOf(polygons) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity

  for (const polygon of polygons) {
    for (const [x, y] of polygon) {
      x0 = Math.min(x0, x); y0 = Math.min(y0, y)
      x1 = Math.max(x1, x); y1 = Math.max(y1, y)
    }
  }

  return { x0, y0, x1, y1 }
}

/** Konvexe Hülle (Andrew's monotone chain) für den Schlagschatten. */
function convexHull(points) {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower = []

  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop()
    }

    lower.push(p)
  }

  const upper = []

  for (const p of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop()
    }

    upper.push(p)
  }

  return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}

function toSvgPoints(points) {
  return points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
}

/**
 * Extrudiert ein Bodenpolygon zu einem Block: sichtbare Seitenflächen, Deckfläche, Schatten.
 * Sichtbar sind die Seiten, deren Außennormale zum Betrachter zeigt.
 */
export function extrude(points, height, projection) {
  if (signedArea(points) < 0) {
    points = [...points].reverse()
  }

  const { project, toward } = projection
  const sides = []

  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    const length = Math.hypot(dx, dy) || 1
    const normal = [dy / length, -dx / length]
    const facing = normal[0] * toward[0] + normal[1] * toward[1]

    if (facing <= 0.001 || height <= 0) {
      continue
    }

    sides.push({
      // Fläche mit Normale nach +x ("rechts") wird dunkler schattiert als die nach +y ("vorn")
      shade: Math.abs(normal[0]) > Math.abs(normal[1]) ? 'right' : 'front',
      points: toSvgPoints([project(...a, height), project(...b, height), project(...b, 0), project(...a, 0)])
    })
  }

  // Schlagschatten: Hülle aus Grundriss und leicht versetztem Grundriss, damit er am Sockel ansetzt
  // (ein frei versetzter Grundriss ließe den Block schweben)
  const offset = Math.min(6, height * 0.3)
  const shadowPoints = convexHull([...points, ...points.map(([x, y]) => [x + offset, y + offset])])

  return {
    sides,
    top: toSvgPoints(points.map(([x, y]) => project(x, y, height))),
    topPoints: points.map(([x, y]) => project(x, y, height)),
    shadow: toSvgPoints(shadowPoints.map(([x, y]) => project(x, y, 0)))
  }
}

/**
 * Zeichenreihenfolge (Maler-Algorithmus) für Blöcke mit sich nicht überlappenden Grundrissen.
 * Ein Block, der vollständig links (kleineres x) oder hinten (kleineres y) liegt, wird zuerst gezeichnet.
 * Bei uneindeutigen Lagen (diagonal, überlappend) entscheidet die Nähe zum Betrachter. Ergebnis per topologischer Sortierung.
 */
export function orderBlocks(blocks, projection) {
  const [tx, ty] = projection.toward
  const n = blocks.length
  const near = blocks.map(b => b.bbox.x1 * tx + b.bbox.y1 * ty)
  const after = Array.from({ length: n }, () => [])
  const indegree = new Array(n).fill(0)
  const eps = 0.5

  const edge = (from, to) => {
    after[from].push(to)
    indegree[to]++
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = blocks[i].bbox
      const b = blocks[j].bbox
      const aBehind = a.x1 <= b.x0 + eps || a.y1 <= b.y0 + eps
      const bBehind = b.x1 <= a.x0 + eps || b.y1 <= a.y0 + eps

      // Nur eindeutige Lagen ergeben eine Kante; der Rest wird über die Nähe zum Betrachter sortiert.
      if (aBehind && !bBehind) {
        edge(i, j)
      } else if (bBehind && !aBehind) {
        edge(j, i)
      }
    }
  }

  const result = []
  const remaining = new Set(blocks.map((_, i) => i))
  const ready = []

  for (let i = 0; i < n; i++) {
    if (indegree[i] === 0) {
      ready.push(i)
    }
  }

  while (remaining.size > 0) {
    if (ready.length === 0) {
      // Zyklus (nicht-transitive Lagebeziehung): nächstgelegenen Rest nehmen
      let best = null

      for (const i of remaining) {
        if (best == null || near[i] < near[best]) {
          best = i
        }
      }

      ready.push(best)
    }

    ready.sort((a, b) => near[a] - near[b])

    const i = ready.shift()

    if (!remaining.has(i)) {
      continue
    }

    remaining.delete(i)
    result.push(blocks[i])

    for (const j of after[i]) {
      indegree[j]--

      if (indegree[j] === 0 && remaining.has(j)) {
        ready.push(j)
      }
    }
  }

  return result
}

export const HEIGHTS = {
  wallThin: 14,      // Innenwände: nur wenig höher als die Stände
  wallOuter: 8,      // Außenwand vorn: niedrig, damit alles sichtbar bleibt
  wallOuterBack: 32, // Außenwand hinten: höher als Abgrenzung der Halle
  stand: 10,
  sagasser: 10,      // alle Stände und Flächen gleich hoch
  service: 10,
  slab: 3
}

const WALL_HEIGHTS = {
  thin: HEIGHTS.wallThin,
  outer: HEIGHTS.wallOuter,
  'outer-back': HEIGHTS.wallOuterBack,
  thick: HEIGHTS.wallThin
}

function areaPolygons(area) {
  if (area.points) {
    return [area.points]
  }

  if (area.parts) {
    return area.parts.map(rectPoints)
  }

  return [rectPoints(area)]
}

/**
 * Baut aus dem Plan-Modell (floorplan.json) die Szene: sortierte Blöcke mit fertigen SVG-Punktlisten.
 */
export function buildScene(plan, projection = createProjection()) {
  const blocks = []

  const add = (type, key, polygons, height, extra = {}) => {
    const parts = polygons.map(p => extrude(p, height, projection))
    const bbox = bboxOf(polygons)
    const center = [(bbox.x0 + bbox.x1) / 2, (bbox.y0 + bbox.y1) / 2]

    blocks.push({
      type,
      key,
      height,
      bbox,
      center,
      anchor: projection.project(center[0], center[1], height),
      sides: parts.flatMap(p => p.sides),
      tops: parts.map(p => p.top),
      topPoints: parts.flatMap(p => p.topPoints),
      shadows: parts.map(p => p.shadow),
      ...extra
    })
  }

  for (const wall of plan.walls) {
    add('wall', `wall-${blocks.length}`, areaPolygons(wall), WALL_HEIGHTS[wall.kind] || HEIGHTS.wallThin, { kind: wall.kind })
  }

  for (const stand of plan.stands) {
    add('stand', `stand-${stand.number}`, [rectPoints(stand)], HEIGHTS.stand, { number: stand.number, label: stand.label || null })
  }

  for (const area of plan.areas) {
    add(area.kind, `area-${area.id}`, areaPolygons(area), area.kind === 'sagasser' ? HEIGHTS.sagasser : HEIGHTS.service, {
      number: area.number || null,
      label: area.label || null
    })
  }

  const ordered = orderBlocks(blocks, projection)
  // Hallenplatte: Polygon (`floor.points`, z. B. mit Stufe) oder das Rechteck `bounds`
  const slab = extrude(plan.floor != null && plan.floor.points != null ? plan.floor.points : rectPoints(plan.bounds), HEIGHTS.slab, projection)
  const screenPoints = [...ordered.flatMap(b => b.topPoints), ...slab.topPoints]
  const bounds = bboxOf([screenPoints])

  let entrance = null

  if (plan.entrance) {
    const cx = plan.entrance.x + plan.entrance.w / 2
    entrance = {
      gap: projection.project(cx, plan.entrance.y, 0),
      label: projection.project(cx, plan.entrance.y + 16, 0)
    }
    bounds.y1 = Math.max(bounds.y1, entrance.label[1] + 14)
  }

  return {
    projection,
    blocks: ordered,
    slab,
    entrance,
    bounds: { x0: bounds.x0 - 8, y0: bounds.y0 - 28, x1: bounds.x1 + 8, y1: bounds.y1 + 8 }
  }
}
